// core/utils/steps/multiStepperStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
import createSessionStorage from "../storage"; // your existing helper

type AnyForm = Record<string, unknown>;

type StepperState<TForm extends AnyForm = AnyForm> = {
  currentStepIndex: number;
  formData: TForm;
};

type MultiStepperState = {
  steppers: Record<string, StepperState>;
  // lifecycle
  ensureStepper: <TForm extends AnyForm>(
    id: string,
    initialForm: TForm
  ) => void;
  resetStepper: (id: string, initialForm: AnyForm) => void;
  // navigation
  setStep: (id: string, index: number) => void;
  nextStep: (id: string, maxSteps: number) => void;
  prevStep: (id: string) => void;
  // data
  updateFormData: <TForm extends AnyForm>(
    id: string,
    partial: Partial<TForm>
  ) => void;
};

export const useMultiStepperStore = create(
  persist<MultiStepperState>(
    (set, get) => ({
      steppers: {},

      ensureStepper: (id, initialForm) => {
        const { steppers } = get();
        if (steppers[id]) return;
        set({
          steppers: {
            ...steppers,
            [id]: { currentStepIndex: 0, formData: initialForm },
          },
        });
      },

      resetStepper: (id, initialForm) => {
        const { steppers } = get();
        if (!steppers[id]) return;
        set({
          steppers: {
            ...steppers,
            [id]: { currentStepIndex: 0, formData: initialForm },
          },
        });
      },

      setStep: (id, index) => {
        const { steppers } = get();
        const s = steppers[id];
        if (!s) return;
        set({
          steppers: {
            ...steppers,
            [id]: { ...s, currentStepIndex: index },
          },
        });
      },

      nextStep: (id, maxSteps) => {
        const { steppers } = get();
        const s = steppers[id];
        if (!s) return;
        set({
          steppers: {
            ...steppers,
            [id]: {
              ...s,
              currentStepIndex: Math.min(s.currentStepIndex + 1, maxSteps - 1),
            },
          },
        });
      },

      prevStep: (id) => {
        const { steppers } = get();
        const s = steppers[id];
        if (!s) return;
        set({
          steppers: {
            ...steppers,
            [id]: {
              ...s,
              currentStepIndex: Math.max(s.currentStepIndex - 1, 0),
            },
          },
        });
      },

      updateFormData: (id, partial) => {
        const { steppers } = get();
        const s = steppers[id];
        if (!s) return;
        set({
          steppers: {
            ...steppers,
            [id]: {
              ...s,
              formData: { ...s.formData, ...partial }, // MERGE
            },
          },
        });
      },
    }),
    {
      name: "multi-stepper-storage",
      storage: createSessionStorage<MultiStepperState>(),
    }
  )
);
